import React from 'react';
import {
	faBoxes,
	faEdit,
	faLink,
	faUpDown,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	Flex,
	Space,
	Tooltip,
} from 'antd';
import classNames from 'classnames';
import { DeleteOutlined } from '@ant-design/icons';
import { Moment } from 'moment-timezone';
import {
	MetersPer,
	PortActionTypeLabels,
	FixtureTypes,
	FuelTypes,
	PortActionTypes,
	TimeZoneFormat,
	BunkerStemQuantityStatus,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { trimString } from '@shared/utils/string';
import {
	isPortCallCommencementDto,
	isPortCallCompletionDto,
	isPortCallDeliveryDto,
	isPortCallRedeliveryDto,
} from '@shared/utils/portCallHelpers';
import {
	fromISOTimezone,
	timeZoneOffsetLabel,
	toISOTimezoneInstant,
} from '@shared/utils/timezones';
import RobBunker from '@api/models/rob-bunker';
import type { UpdatePortCallRequest } from '@api/features/ops/updatePortCall';
import {
	ItineraryPortCallDto,
	ItinerarySeaPassageDto,
} from '@client/screens/estimates/details/helpers/types';
import DatePicker from '@client/components/DatePicker';
import Button from '@client/components/Button';
import { EditableColumns } from '@client/components/EditableTableRedux/EditableCellTableRedux';
import EstimatedActualTag from '@client/components/EstimatedActualTag';
import TooltipIcon from '@client/components/TooltipIcon';
import CargoContractSideTag from '@client/screens/cargos/details/utils/CargoContractSideTag';
import styles from '../components/styles/itineraryColumns.module.css';
import DateBadge from '../components/DateBadge';
import SetEtdPopover from '../components/SetEtdPopover';
import { RobChange } from '../ItineraryProvider';
import { getPortDaysForPortCallEtd, getSetEtdDisabledReason } from './setEtd';
export type AddonLabel = 'Est.' | 'Act.' | undefined;

// We don't want to show ALL actions, only ones related to itinerary planning
const actionsToShow = [
	PortActionTypes.COMMENCEMENT,
	PortActionTypes.COMPLETION,
	PortActionTypes.BUNKER_STEM,
	PortActionTypes.DELIVERY,
	PortActionTypes.REDELIVERY,
	PortActionTypes.REDELIVERING_TC_IN,
	PortActionTypes.ARRIVAL,
	PortActionTypes.DEPARTURE,
	PortActionTypes.LOADING,
	PortActionTypes.DISCHARGING,
	PortActionTypes.CANAL_TRANSIT,
	PortActionTypes.PASSING,
	PortActionTypes.REPOSITIONING,
	PortActionTypes.WAITING,
	PortActionTypes.DRY_DOCKING,
] as string[];

const getItineraryColumns = ({
	portCalls,
	onSelectEntry,
	seaPassages,
	onUpdatePortCall,
	onUpdatePortCallAttributes,
	fixtureType,
	deletePortCall,
	viewMode,
	setShowCommencementLinking,
	setEditingCargoId,
	setEditingCargoPortId,
	uniqueFuelTypes = [],
	timeZoneFormat,
	saveRobChanges,
	isBeforeMinPeriod = false,
	isBeyondMaxPeriod = false,
}: {
	portCalls: ItineraryPortCallDto[];
	onSelectEntry: (entry: ItinerarySeaPassageDto | ItineraryPortCallDto | undefined) => void;
	seaPassages: ItinerarySeaPassageDto[];
	onUpdatePortCall: (field: string, value: any, newRow: ItineraryPortCallDto) => void;
	onUpdatePortCallAttributes: (
		attributes: Omit<UpdatePortCallRequest['attributes'], 'PortCallActions'>,
		newRow: ItineraryPortCallDto,
	) => void;
	fixtureType: Values<typeof FixtureTypes> | undefined;
	deletePortCall: (portCall: ItineraryPortCallDto) => void;
	viewMode: 'default' | 'map' | 'bunker';
	setShowCommencementLinking: (show: boolean) => void;
	setEditingCargoId: React.Dispatch<React.SetStateAction<number | null>>;
	setEditingCargoPortId: React.Dispatch<React.SetStateAction<number | null>>;
	uniqueFuelTypes: Array<Values<typeof FuelTypes>>;
	timeZoneFormat: TimeZoneFormat;
	saveRobChanges: (changes?: RobChange[]) => Promise<void>;
	isBeforeMinPeriod?: boolean;
	isBeyondMaxPeriod?: boolean;
}) => {
	const round = (n: number) => Math.round(n * 1000) / 1000;

	const onSetEtd = (row: ItineraryPortCallDto, targetEtd: Moment) => {
		const result = getPortDaysForPortCallEtd(row, targetEtd);

		if (result == null) {
			return null;
		}

		if (result.error != null) {
			return result.error;
		}

		onUpdatePortCallAttributes({
			...result.days,
			manualEstimatedDepartureDate: targetEtd,
		}, row);

		return null;
	};

	const getEstActLabel = (rob?: RobBunker): React.ReactElement | null => (
		rob ?
			<EstimatedActualTag estimated={rob.forecasted} /> :
			null
	);

	const addonBefore = (label: React.ReactElement | null) => label ? (
		<div
			data-testid="bunker-addon-label"
		>
			{label}
		</div>
	) : undefined;

	const hasNegativeRob = (pc: ItineraryPortCallDto, type: 'arrival' | 'departure') => uniqueFuelTypes.some((ft) => {
		const val = Number((pc as Record<string, unknown>)[`${ft}-${type}`]);

		return val < 0;
	});

	const negativeRobPortCallIds = new Set<number>();
	let foundNegativeRob = false;

	portCalls.forEach((pc) => {
		if (foundNegativeRob) {
			negativeRobPortCallIds.add(pc.id);

			return;
		}

		if (hasNegativeRob(pc, 'departure')) {
			negativeRobPortCallIds.add(pc.id);
			foundNegativeRob = true;

			return;
		}

		const sp = seaPassages.find((s) => s.id === pc.nextSeaPassageId);
		const nextPc = sp == null ? undefined : portCalls.find((p) => p.id === sp.nextPortCallId);

		if (nextPc != null && hasNegativeRob(nextPc, 'arrival')) {
			negativeRobPortCallIds.add(pc.id);
			foundNegativeRob = true;
		}
	});

	// @ts-ignore
	const arrivalColumns: EditableColumns<ItineraryPortCallDto> = uniqueFuelTypes
		.map((fuelType) => ({
			key: `${fuelType}-arrival`,
			title: `${fuelType}`,
			dataIndex: `${fuelType}-arrival`,
			editable: true,
			type: 'number' as 'number',
			inputProps: {
				variant: 'borderless',
				allowNegative: false,
			},
			inputPropsWithRow: (row: ItineraryPortCallDto) => {
				const isCommencement = isPortCallCommencementDto(row);
				const hidden = isCommencement;

				const noArrivalDate = row.arrivalDate == null;
				const disabled = noArrivalDate || isCommencement;

				const arrivalRob = row.PortCallActions
					.filter((pca) => pca.event === PortActionTypes.ARRIVAL || pca.event === PortActionTypes.COMMENCEMENT)
					.flatMap((pca) => pca.RobBunkers ?? [])
					.find((b) => b.fuelGrade === fuelType);

				const addonLabel = getEstActLabel(arrivalRob);

				return {
					addonBefore: !hidden ? addonBefore(addonLabel) : null,
					allowClear: !arrivalRob?.forecasted,
					asAsync: !disabled,
					...(disabled && { disabled }),
					valueOverride: hidden ? '-' : undefined,
					...(!hidden && { 'data-testid': `arrival-rob-${row.port.name}-${fuelType}` }),
					onClear: async () => {
						const key = `${fuelType}-arrival`;
						await saveRobChanges([{
							key,
							portCallId: row.id,
							newQuantity: arrivalRob?.forecastedQuantity ?? null,
							forecasted: true,
						}]);
					},
				};
			},
		}));

	// @ts-ignore
	const departureColumns: EditableColumns<ItineraryPortCallDto> = uniqueFuelTypes
		.map((fuelType) => ({
			key: `${fuelType}-departure`,
			title: `${fuelType}`,
			dataIndex: `${fuelType}-departure`,
			editable: true,
			type: 'number' as 'number',
			inputProps: {
				variant: 'borderless',
				allowNegative: false,
			},
			inputPropsWithRow: (row: ItineraryPortCallDto) => {
				const hidden = !row.PortCallActions.some((pca) => (pca.RobBunkers ?? []).length > 0);
				const isCommencement = isPortCallCommencementDto(row);
				const noDepartureDate = row.departureDate == null;
				const disabled = noDepartureDate || isCommencement;

				const departureRob = row.PortCallActions
					.filter((pca) =>
						pca.event === PortActionTypes.DEPARTURE ||
						pca.event === PortActionTypes.COMPLETION ||
						pca.event === PortActionTypes.COMMENCEMENT
					)
					.flatMap((pca) => pca.RobBunkers ?? [])
					.find((b) => b.fuelGrade === fuelType);

				const addonLabel = getEstActLabel(departureRob);

				const hasValue = row.PortCallActions.some((pca) =>
					(pca.RobBunkers ?? []).some((b) => b.fuelGrade === fuelType)
				);

				if (!hasValue) {
					return { disabled: true, valueOverride: '-' };
				}

				return {
					allowClear: !departureRob?.forecasted,
					addonBefore: addonBefore(addonLabel),
					asAsync: !disabled,
					...(disabled && { disabled }),
					valueOverride: hidden ? '-' : undefined,
					...(!hidden && { 'data-testid': `departure-rob-${row.port.name}-${fuelType}` }),
					onClear: async () => {
						const key = `${fuelType}-departure`;
						await saveRobChanges([{
							key,
							portCallId: row.id,
							newQuantity: departureRob?.forecastedQuantity ?? null,
							forecasted: true,
						}]);
					},
				};
			},
		}));

	// @ts-ignore
	const stemColumns: EditableColumns<ItineraryPortCallDto> = uniqueFuelTypes
		.map((fuelType) => ({
			key: `${fuelType}-stem`,
			title: `${fuelType}`,
			dataIndex: `${fuelType}-stem`,
			type: 'number' as 'number',
			editable: true,
			inputProps: {
				variant: 'borderless',
			},
			inputPropsWithRow: (row: ItineraryPortCallDto) => {
				const parcels = row.PortCallActions
					.flatMap((pca) => pca.BunkerStem?.BunkerStemParcels ?? [])
					.filter((p) => p.fuelGrade === fuelType);

				if (parcels.length === 0) {
					return { disabled: true, valueOverride: '-' };
				}

				const total = parcels.reduce((sum, p) => sum + Number(p.quantity ?? 0), 0);
				const isEstimated = parcels.some((p) => p.quantityStatus === BunkerStemQuantityStatus.ESTIMATED);

				return {
					disabled: true,
					addonBefore: addonBefore(<EstimatedActualTag estimated={isEstimated} slim />),
					valueOverride: round(total).toFixed(3),
					'data-testid': `stem-${row.port.name}-${fuelType}`,
				};
			},
		}));

	const bunkerColumns = [
		...(arrivalColumns.length > 0 ? [{
			title: 'ROB on Arrival',
			children: arrivalColumns,
			editable: true,
		}] : []),
		...(stemColumns.length > 0 ? [{
			title: 'Stems',
			children: stemColumns,
			editable: true,
		}] : []),
		...(departureColumns.length > 0 ? [{
			title: 'ROB on Departure',
			children: departureColumns,
			editable: true,
		}] : []
		),
	] as const;

	return [
		{
			key: 'actions',
			align: 'center' as const,
			width: 30,
			render: (row: ItineraryPortCallDto) => (
				<>
					{isPortCallCommencementDto(row) ? (
						<FontAwesomeIcon
							className={styles.cursor}
							onClick={() => setShowCommencementLinking(true)}
							icon={faEdit}
						/>
					) : (
						<FontAwesomeIcon
							className={styles.cursor}
							onClick={() => onSelectEntry(row)}
							icon={faEdit}
							data-testid={`open-modal-${row.port.name}`}
						/>
					)}
				</>
			),
		},
		{
			title: 'Port',
			dataIndex: 'port',
			width: 250,
			render: (row: ItineraryPortCallDto) => {
				const { port } = row;

				if (port == null) {
					return null;
				}

				const isNegativeRob = negativeRobPortCallIds.has(row.id);
				const portName = (
					<span
						className={isNegativeRob ? styles.red : undefined}
					>
						<Tooltip title={isNegativeRob ? 'Bunkers go below zero. You may need to stem bunkers.' : undefined}>
							{trimString(port?.name, 20)}
						</Tooltip>
					</span>
				);

				const isCompletion = isPortCallCompletionDto(row);

				if (isCompletion) {
					return portName;
				}

				const seaPassage = seaPassages.find((sp) => sp.id === row.nextSeaPassageId);

				if (seaPassage == null) {
					return portName;
				}

				const nextPortCall = seaPassage == null ? undefined : portCalls.find((pc) => (
					pc.id === seaPassage.nextPortCallId
				));

				const { departureDate } = row;
				const arrivalDate = nextPortCall != null ? nextPortCall.arrivalDate : undefined;

				const estimatedDepartureDate = row.departureDate ?? row.estimatedDepartureDate;
				const estimatedArrivalDate = nextPortCall?.estimatedArrivalDate;

				const estimatedDuration = (estimatedArrivalDate != null && estimatedArrivalDate != null) ?
					round((estimatedArrivalDate).diff(estimatedDepartureDate, 'days', true)) :
					undefined;

				const distance = seaPassage.route == null ? 0 : Math.round(
					seaPassage?.route?.properties?.distance / MetersPer.NAUTICAL_MILE,
				);

				const isSeaPassageActual = (
					row.departureDate != null && nextPortCall?.arrivalDate != null
				);

				const actualDuration = arrivalDate != null ? round(arrivalDate.diff(departureDate, 'days', true)) : undefined;

				return (
					<Tooltip title={port?.name.length > 20 ? port?.name : undefined}>
						<div
							title={port.name}
							className={styles.portContainer}
						>
							{portName}
							{seaPassage && (
								<div
									role="button"
									onClick={() => onSelectEntry(seaPassage)}
									onKeyPress={(e) => e.key === 'Enter' && onSelectEntry(seaPassage)}
									tabIndex={0}
									className={classNames(styles.seaPassagePill, {
										[styles.estimated]: !isSeaPassageActual,
										[styles.actual]: isSeaPassageActual,
									})}
								>
									<Space>
										<FontAwesomeIcon icon={faUpDown} />
										{isSeaPassageActual ?
											`${actualDuration ?? estimatedDuration} days` :
											`Est. ${distance} nm | ${estimatedDuration} days`}
									</Space>
								</div>
							)}
						</div>
					</Tooltip>
				);
			},
		},
		...(viewMode === 'default' ? [
			{
				title: 'Arrival',
				dataIndex: 'estimatedArrivalDate',
				key: 'estimatedArrivalDate',
				width: 320,
				render: (row: ItineraryPortCallDto) => {
					if (row == null) {
						return null;
					}

					const isCommencement = isPortCallCommencementDto(row);
					const isDelivery = isPortCallDeliveryDto(row);

					if (isCommencement || row.prevSeaPassageId == null) {
						return (<div style={{ width: '100%', textAlign: 'center' }}>—</div>);
					}

					const dateToUse = row.arrivalDate ?? row.mastersEta ?? row.estimatedArrivalDate;
					const value = dateToUse ?
						fromISOTimezone(dateToUse.toISOString(true), timeZoneFormat, row.port.timezone) : null;

					return (
						<Flex align="center" className={classNames(styles.inlineRow, {
							[styles.deliveryDate]: isDelivery,
						})}>
							<DateBadge entry={row} type="arrival" />
							<div className={styles.inputWrap}>
								<DatePicker
									time
									showTimezone={false}
									variant="borderless"
									allowClear={row.arrivalDate != null}
									value={value}
									onChange={(d) => onUpdatePortCall('arrivalDate', d != null ?
										toISOTimezoneInstant(d as Moment, timeZoneFormat, row.port.timezone) :
										null, row)}
									forceOnChange
									disabled={isPortCallCommencementDto(row)}
									data-testid={`arrival-date-${row.port.name}`}
								/>
								<span className={styles.tzLabel}>
									{timeZoneOffsetLabel(value, row.port.timezone, timeZoneFormat)}
								</span>
							</div>
						</Flex>
					);
				},
			},
			{
				title: 'Departure',
				dataIndex: 'estimatedDepartureDate',
				key: 'estimatedDepartureDate',
				width: 340,
				render: (row: ItineraryPortCallDto) => {
					if (row == null || row.port == null) {
						return null;
					}

					const isCommencement = isPortCallCommencementDto(row);
					const isRedelivery = isPortCallRedeliveryDto(row);
					const dateToUse = row.departureDate ?? row.estimatedDepartureDate;
					const value = dateToUse == null ?
						undefined :
						fromISOTimezone((dateToUse).toISOString(true), timeZoneFormat, row.port.timezone);

					return (
						<Flex align="center" className={classNames(styles.inlineRow, {
							[styles.redeliveryDate]: isRedelivery,
						})}>
							<DateBadge entry={row} type="departure" />
							<div className={styles.inputWrap}>
								<DatePicker
									time
									showTimezone={false}
									variant="borderless"
									allowClear={row.departureDate != null}
									value={value}
									onChange={(d) => onUpdatePortCall('departureDate', d != null ?
										toISOTimezoneInstant(d as Moment, timeZoneFormat, row.port.timezone) :
										null, row)}
									forceUtcUpdate={false}
									forceOnChange
									disabled={isCommencement}
									data-testid={`departure-date-${row.port.name}`}
								/>
								<span className={styles.tzLabel}>
									{value ? timeZoneOffsetLabel(value, row.port.timezone, timeZoneFormat) : ''}
								</span>
								{' '}
								<div className={styles.iconSlot}>
									{isRedelivery && (isBeforeMinPeriod || isBeyondMaxPeriod) ? (
										<TooltipIcon
											danger
											data-testid="outside-declared-period-tooltip"
										>
											Hire falls outside min./max. declared period.
											{` Please ${isBeforeMinPeriod ? 'reduce' : 'extend'} declared hire period.`}
										</TooltipIcon>
									) : !isCommencement && row.prevSeaPassageId != null && (
										<SetEtdPopover
											currentEtd={row.estimatedDepartureDate}
											timezone={row.port.timezone}
											disabledReason={getSetEtdDisabledReason(row, fixtureType)}
											onSet={(targetEtd) => onSetEtd(row, targetEtd)}
										/>
									)}
									{isCommencement && (
										<Button
											data-testid="contract-link-btn"
											onClick={() => setShowCommencementLinking(true)}
											type="link"
											icon={(<FontAwesomeIcon size="lg" icon={faLink} />)}
										/>
									)}
								</div>
							</div>
						</Flex>
					);
				},
			},
		] : []),
		...(viewMode === 'bunker' ?
			bunkerColumns : [
				{
					title: 'Purpose(s)',
					dataIndex: 'actions',
					key: 'actions',
					render: (row: ItineraryPortCallDto) => {
						if (!row?.actions) {
							return null;
						}

						const { actions } = row;

						const isCommencement = isPortCallCommencementDto(row);

						const filtered = actions.filter((a) => {
							if (a.event === PortActionTypes.ARRIVAL || a.event === PortActionTypes.DEPARTURE) {
								return false;
							}

							if (a.event === PortActionTypes.LOADING || a.event === PortActionTypes.DISCHARGING) {
								return true;
							}

							return actionsToShow.includes(a.event);
						});

						const labels = filtered
							.map((a) => PortActionTypeLabels[a.event as keyof typeof PortActionTypeLabels] ||
								a.event);

						if (row.cargoActivity) {
							labels.push(row.cargoActivity);
						}

						return labels.length > 0 ? labels.join(', ') : (!isCommencement && !row.cargoActivity ? ' — ' : '');
					},
				},
				{
					title: 'Cargo',
					dataIndex: ['actions'],
					key: 'cargo',
					render: (row: ItineraryPortCallDto) => {
						if ((row == null || row.actions == null) && row.cargoCommodity == null) {
							return null;
						}

						if (row.cargoCommodity != null) {
							return row.cargoCommodity;
						}

						const { actions } = row;

						const sorted = [...actions].sort((a, b) => a?.Cargo?.type?.localeCompare(b?.Cargo?.type));

						return sorted.map((action) => {
							if (row == null || row.actions == null) {
								return null;
							}

							const cargo = action?.Cargo;

							if (cargo == null) {
								return undefined;
							}

							const cargoPorts = cargo.CargoPorts;
							const relevantCp = cargoPorts.find((cp) => cp.portCallId === action.portCallId);

							return (
								<Button
									padding={0}
									type="link"
									onClick={() => {
										setEditingCargoId(action.cargoId);
										setEditingCargoPortId(relevantCp?.id ?? null);
									}}
								>
									<Space>
										<FontAwesomeIcon icon={faBoxes} />
										{cargo?.contractSide != null && <CargoContractSideTag contractSide={cargo?.contractSide} />}
										{cargo.type}
									</Space>
								</Button>
							);
						});
					},
				},
				{
					key: 'kebab',
					title: '',
					width: 25,
					render: (row: ItineraryPortCallDto) => (
						<Button
							data-testid={`delete-pc-${row?.port?.name}`}
							onClick={() => deletePortCall(row)}
							type="link"
							confirmTitle="Are you sure you want to delete this port call?"
							danger
							icon={(<DeleteOutlined />)}
						/>
					),
				},
			]),
	];
};

export default getItineraryColumns;
